import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { PaymentDialog, PriceItem } from '../PaymentDialog';
import { LoadPricing } from '../../../slices/pricing/load-pricing.thunk';
import createNotification from '../../../utils/createNotification';
import { VideoClip } from '../../../containers/PracticeManagementGroups/Conferences/SaveVideoClipPopup/SaveVideoClipPopup';
import { Pricing } from '../../../slices/pricing/pricing.model';
import { useStateSelector } from '../../../store/selectors';

interface SaveClipPaymentComponentProps {
    isVisible: boolean;
    setIsVisible: (isVisible: React.SetStateAction<boolean>) => void;
    conferenceId: number;
    clip: VideoClip;
    clipName: string;
    clipDescription: string;
    groupId: number;
    paymentSuccess: () => void;
}

export const SaveClipPaymentComponent = (
    props: SaveClipPaymentComponentProps
) => {
    const axios = useStateSelector((state) => state.core.axios);
    const pricing: Pricing = useStateSelector((state) => state.pricing.pricing);
    const dispatch: any = useDispatch();
    const stripe = useStripe();
    const elements = useElements();

    const [originalServicePrice, setOriginalServicePrice] = useState(0);
    const [totalPrice, setTotalPrice] = useState(0);
    const [appliedCredits, setAppliedCredits] = useState(0);
    const [isPaymentInProcess, setIsPaymentInProcess] = useState(false);

    useEffect(() => {
        dispatch(LoadPricing());
    }, []);

    useEffect(() => {
        setOriginalServicePrice(Number(pricing.clipSave));
    }, [pricing]);

    const calculateTotalPrice = () => originalServicePrice - appliedCredits;

    useEffect(() => {
        setTotalPrice(calculateTotalPrice());
    }, [appliedCredits, originalServicePrice]);

    const priceList: PriceItem[] = [
        {
            priceType: 'summary',
            label: 'Cost of Clip Saving:',
            value: originalServicePrice,
            valueType: 'currency',
        },
    ];

    const purchaseRequest = (
        shouldUseSavedPaymentMethod: boolean,
        paymentTokenId: string = ''
    ) => {
        setIsPaymentInProcess(true);
        const videoClip = {
            ...props.clip,
            title: props.clipName,
            description: props.clipDescription,
        };
        axios
            .post(`/api/groups/${props.groupId}/clips`, videoClip)
            .then((response: any) => {
                if (response.status === 200) {
                    axios
                        .post(
                            `/api/groups/${props.groupId}/clips/clipCreationPayment`,
                            {
                                clipId: response.data,
                                groupId: props.groupId,
                                conferenceId: props.clip.conferenceId,
                                useSavedPaymentMethod:
                                    shouldUseSavedPaymentMethod,
                                walletAmount: appliedCredits,
                                price: originalServicePrice,
                                paymentSystemMethodId: paymentTokenId,
                            }
                        )
                        .then((response: any) => {
                            if (response.status === 200) {
                                createNotification(
                                    'Video Clip Created Successfully',
                                    'success'
                                );
                                props.paymentSuccess();
                            } else {
                                createNotification(
                                    response.response.data?.title ??
                                        'Error occured while creation Video Clip',
                                    'error'
                                );

                                setIsPaymentInProcess(false);
                                props.setIsVisible(false);
                            }
                        })
                        .catch(() => {
                            createNotification(
                                'Error occured while creation Video Clip',
                                'error'
                            );
                        })
                        .finally(() => {
                            setIsPaymentInProcess(false);
                        });
                } else {
                    createNotification(
                        'Error occured while creating Virtual Meeting Clip',
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    'Error occured while creating Virtual Meeting Clip',
                    'error'
                );
            });
    };

    const directPayment = () => purchaseRequest(false);

    return (
        <div className="save-clip-payment-component">
            <PaymentDialog
                isVisible={props.isVisible}
                setIsVisible={props.setIsVisible}
                stripe={stripe}
                elements={elements}
                priceList={priceList}
                directPayment={directPayment}
                originalServicePrice={originalServicePrice}
                servicePrice={originalServicePrice}
                totalProductPrice={totalPrice}
                apliedCredits={appliedCredits}
                setAppliedCredits={(value) => setAppliedCredits(value)}
                chargeRequest={purchaseRequest}
                isParrentLoading={false}
                isPaymentInProcess={isPaymentInProcess}
                isAuthorizationRequired={false}></PaymentDialog>
        </div>
    );
};

export default SaveClipPaymentComponent;
